import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Wrench, AlertTriangle, CheckCircle, Database, Link2, Phone, Calendar, CreditCard, Mail, Bot } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const INTEGRATION_ICONS = {
  twilio: Phone,
  calendly: Calendar,
  stripe: CreditCard,
  resend: Mail,
  openai: Bot,
};

const AdminMaintenance = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  const [health, setHealth] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/maintenance`, { headers }).then(r => setMaintenance(r.data)).catch(() => {});
    axios.get(`${API}/admin/system-health`, { headers }).then(r => setHealth(r.data)).catch(() => {});
    axios.get(`${API}/workflows/integrations`, { headers }).then(r => setIntegrations(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/maintenance`, maintenance, { headers });
      toast({ title: maintenance.enabled ? 'Maintenance mode enabled' : 'Maintenance mode disabled' });
    } catch (err) { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-maintenance-page">
      <h1 className="text-2xl font-bold text-white mb-1">Maintenance</h1>
      <p className="text-gray-500 text-sm mb-6">System maintenance and health monitoring</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Maintenance mode */}
        <Card className="bg-[#132218] border-emerald-900/30">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Wrench className="w-4 h-4 text-emerald-400" />Maintenance Mode</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setMaintenance(m => ({...m, enabled: !m.enabled}))} className={`w-12 h-6 rounded-full transition-colors relative ${maintenance.enabled ? 'bg-amber-500' : 'bg-gray-600'}`} data-testid="maintenance-toggle">
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${maintenance.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
              <span className={`text-sm font-medium ${maintenance.enabled ? 'text-amber-400' : 'text-gray-400'}`}>
                {maintenance.enabled ? 'Enabled — users will see maintenance page' : 'Disabled — system running normally'}
              </span>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Maintenance Message</Label>
              <Textarea value={maintenance.message || ''} onChange={e => setMaintenance(m => ({...m, message: e.target.value}))} rows={3} placeholder="We're upgrading our systems. Back shortly!" className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="maintenance-message" />
            </div>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="save-maintenance-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </CardContent>
        </Card>

        {/* System health */}
        <Card className="bg-[#132218] border-emerald-900/30">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Database className="w-4 h-4 text-emerald-400" />System Health</CardTitle></CardHeader>
          <CardContent>
            {health ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium text-sm">All systems operational</span>
                </div>
                <div className="space-y-2">
                  {Object.entries(health.collections || {}).map(([col, count]) => (
                    <div key={col} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-black/20">
                      <span className="text-gray-400 text-xs capitalize">{col.replace('_', ' ')}</span>
                      <span className="text-white text-xs font-mono">{count.toLocaleString()} docs</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Loading health data...</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Connected Services — 3rd party integrations */}
      <Card className="bg-[#132218] border-emerald-900/30 mt-6" data-testid="admin-connected-services">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Link2 className="w-4 h-4 text-emerald-400" />Connected Services
          </CardTitle>
          <p className="text-gray-500 text-xs">Third-party integrations powering the platform</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations.map(intg => {
              const Icon = INTEGRATION_ICONS[intg.id] || Link2;
              return (
                <div key={intg.id} className="p-4 rounded-lg bg-black/20 border border-emerald-900/30" data-testid={`admin-integration-${intg.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${intg.connected ? 'bg-emerald-500/15' : 'bg-gray-500/15'}`}>
                        <Icon className={`w-4 h-4 ${intg.connected ? 'text-emerald-400' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <h3 className="text-white font-medium text-sm">{intg.name}</h3>
                        <p className="text-gray-500 text-xs">{intg.category}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                      intg.connected
                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'
                        : 'bg-gray-500/15 text-gray-400 border-gray-500/20'
                    }`}>
                      {intg.connected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs">{intg.description}</p>
                </div>
              );
            })}
            {integrations.length === 0 && <p className="text-gray-500 text-sm">Loading integrations...</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMaintenance;
